import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, MessageCircle, CheckCircle2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { PICKUP_OPTIONS, BUDGET_OPTIONS, INTEREST_OPTIONS, waLink } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#DC2626]/25 focus:border-gold focus:ring-1 focus:ring-gold/40 rounded-xl px-4 py-3 text-sm text-ivory placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300";

const initial = {
  fullName: "",
  pax: "",
  mobile: "",
  checkIn: "",
  checkOut: "",
  pickup: "",
  pickupOther: "",
  budget: "",
  interests: [],
  notes: "",
  consent: false,
};

export default function EnquiryForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const onPlanExperience = (e) => {
      const interest = e.detail;
      if (!interest) return;
      setDone(null);
      setForm((f) =>
        f.interests.includes(interest) ? f : { ...f, interests: [...f.interests, interest] }
      );
      toast.success(`"${interest}" added to your interests`);
      setTimeout(() => {
        document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    };
    window.addEventListener("rtg:plan-experience", onPlanExperience);
    return () => window.removeEventListener("rtg:plan-experience", onPlanExperience);
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleInterest = (i) =>
    set("interests", form.interests.includes(i) ? form.interests.filter((x) => x !== i) : [...form.interests, i]);

  const validate = () => {
    const e = {};
    if (form.fullName.trim().length < 2) e.fullName = "Please enter your full name";
    const pax = parseInt(form.pax, 10);
    if (!pax || pax < 1) e.pax = "Enter number of passengers";
    if (!/^[6-9]\d{9}$/.test(form.mobile.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "")))
      e.mobile = "Enter a valid 10-digit Indian mobile number";
    if (!form.checkIn) e.checkIn = "Select a check-in date";
    if (!form.checkOut) e.checkOut = "Select a check-out date";
    else if (form.checkIn && form.checkOut <= form.checkIn) e.checkOut = "Check-out must be after check-in";
    if (!form.pickup) e.pickup = "Select your pickup point";
    if (form.pickup === "Other / Specify Location" && !form.pickupOther.trim())
      e.pickupOther = "Please specify your pickup location";
    if (!form.budget) e.budget = "Select your budget";
    if (!form.consent) e.consent = "Please allow us to contact you about this enquiry";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildMessage = () => {
    const pickup = form.pickup === "Other / Specify Location" ? `Other: ${form.pickupOther}` : form.pickup;
    return [
      "New Goa Holiday Enquiry — RAJ TRAVELS GOA",
      `Name: ${form.fullName}`,
      `Passengers: ${form.pax}`,
      `Mobile: +91 ${form.mobile}`,
      `Check-in: ${form.checkIn}`,
      `Check-out: ${form.checkOut}`,
      `Pickup Point: ${pickup}`,
      `Budget: ${form.budget}`,
      `Interests: ${form.interests.length ? form.interests.join(", ") : "Not specified"}`,
      form.notes ? `Notes: ${form.notes}` : null,
    ].filter(Boolean).join("\n");
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSending(true);
    const waUrl = waLink(buildMessage());
    let stored = false;
    try {
      const res = await axios.post(`${API}/leads`, {
        full_name: form.fullName.trim(),
        pax: parseInt(form.pax, 10),
        mobile: form.mobile,
        check_in: form.checkIn,
        check_out: form.checkOut,
        pickup_location: form.pickup,
        pickup_other: form.pickupOther || null,
        budget: form.budget,
        interests: form.interests,
        notes: form.notes || null,
        consent: form.consent,
      });
      stored = res.data?.status === "success";
    } catch (err) {
      stored = false;
    }
    setSending(false);
    setDone({ waUrl, stored });
    window.open(waUrl, "_blank", "noopener");
    if (stored) toast.success("Enquiry received! We have your details.");
    else toast.info("Almost there — tap the WhatsApp button to send your enquiry.");
  };

  if (done) {
    return (
      <section id="enquiry" data-testid="enquiry-section" className="py-14 sm:py-20 px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-xl mx-auto text-center rounded-3xl border border-gold/30 bg-navy-card p-8 sm:p-12"
          data-testid="enquiry-success-panel"
        >
          <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
          <h2 className="font-display text-3xl font-bold mt-6">Thank You, {form.fullName.split(" ")[0]}!</h2>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            Your Goa holiday enquiry is ready{done.stored ? " and our team has been notified" : ""}.
            Send it on WhatsApp now so our travel expert can start planning your trip right away.
          </p>
          <a
            href={done.waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-success-whatsapp-btn"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-8 py-4 rounded-full hover:brightness-110 transition w-full sm:w-auto"
          >
            <MessageCircle className="w-5 h-5" /> Send Enquiry on WhatsApp
          </a>
          <button
            onClick={() => { setDone(null); setForm(initial); }}
            data-testid="enquiry-plan-another-btn"
            className="mt-4 block mx-auto text-xs text-slate-400 hover:text-gold transition-colors underline underline-offset-4"
          >
            Plan another trip
          </button>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-14 sm:py-20 px-5 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-8 sm:mb-10"
        >
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.32em] text-gold font-semibold mb-3">Start Planning</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Plan Your Perfect <span className="gold-text italic">Goa Holiday</span>
          </h2>
          <p className="mt-3 text-sm text-slate-400 max-w-lg mx-auto leading-relaxed">
            Tell us a little about your trip — we will find your right Goa holiday package.
          </p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          noValidate
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="enquiry-form"
          className="rounded-3xl border border-gold/20 bg-navy-card/80 backdrop-blur p-5 sm:p-8 space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fullName" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Full Name *</label>
              <input id="fullName" data-testid="enquiry-fullname-input" className={inputCls} placeholder="e.g., Rohit Sharma"
                value={form.fullName} onChange={(e) => set("fullName", e.target.value)} autoComplete="name" />
              {errors.fullName && <p className="text-xs text-red-500 mt-1.5" data-testid="error-fullname">{errors.fullName}</p>}
            </div>
            <div>
              <label htmlFor="pax" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Number of Passengers (Pax) *</label>
              <input id="pax" data-testid="enquiry-pax-input" type="number" min="1" inputMode="numeric" className={inputCls} placeholder="e.g., 4"
                value={form.pax} onChange={(e) => set("pax", e.target.value)} />
              {errors.pax && <p className="text-xs text-red-500 mt-1.5" data-testid="error-pax">{errors.pax}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="mobile" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Mobile Number *</label>
            <div className="flex">
              <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-[#DC2626]/25 bg-red-50 text-sm text-slate-300">+91</span>
              <input id="mobile" data-testid="enquiry-mobile-input" type="tel" inputMode="numeric" maxLength="10"
                className={`${inputCls} rounded-l-none`} placeholder="10-digit mobile number"
                value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} autoComplete="tel-national" />
            </div>
            {errors.mobile && <p className="text-xs text-red-500 mt-1.5" data-testid="error-mobile">{errors.mobile}</p>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="checkIn" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Check-in Date *</label>
              <input id="checkIn" data-testid="enquiry-checkin-input" type="date" className={inputCls}
                min={new Date().toISOString().split("T")[0]}
                value={form.checkIn} onChange={(e) => set("checkIn", e.target.value)} />
              {errors.checkIn && <p className="text-xs text-red-500 mt-1.5" data-testid="error-checkin">{errors.checkIn}</p>}
            </div>
            <div>
              <label htmlFor="checkOut" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Check-out Date *</label>
              <input id="checkOut" data-testid="enquiry-checkout-input" type="date" className={inputCls}
                min={form.checkIn || new Date().toISOString().split("T")[0]}
                value={form.checkOut} onChange={(e) => set("checkOut", e.target.value)} />
              {errors.checkOut && <p className="text-xs text-red-500 mt-1.5" data-testid="error-checkout">{errors.checkOut}</p>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 items-start">
            <div>
              <label htmlFor="pickup" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Pickup Location in Goa *</label>
              <select id="pickup" data-testid="enquiry-pickup-select" className={inputCls}
                value={form.pickup} onChange={(e) => set("pickup", e.target.value)}>
                <option value="" disabled>Select pickup point</option>
                {PICKUP_OPTIONS.map((o) => <option key={o} value={o}>{o.replace(/ - /g, " — ").replace(/Rs /g, "₹")}</option>)}
              </select>
              {form.pickup === "Other / Specify Location" && (
                <input data-testid="enquiry-pickup-other-input" className={`${inputCls} mt-3`} placeholder="Tell us your pickup point (hotel, landmark...)"
                  value={form.pickupOther} onChange={(e) => set("pickupOther", e.target.value)} />
              )}
              {errors.pickup && <p className="text-xs text-red-500 mt-1.5" data-testid="error-pickup">{errors.pickup}</p>}
              {errors.pickupOther && <p className="text-xs text-red-500 mt-1.5" data-testid="error-pickup-other">{errors.pickupOther}</p>}
              <p className="mt-2 text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 shrink-0 mt-px text-gold/50" />
                Pickup & drop are confirmed with our travel team after your enquiry.
              </p>
            </div>
            <div>
              <label htmlFor="budget" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Budget *</label>
              <select id="budget" data-testid="enquiry-budget-select" className={inputCls}
                value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                <option value="" disabled>Select your budget</option>
                {BUDGET_OPTIONS.map((o) => <option key={o} value={o}>{o.replace(/Rs /g, "₹").replace(/ - /g, "–")}</option>)}
              </select>
              {errors.budget && <p className="text-xs text-red-500 mt-1.5" data-testid="error-budget">{errors.budget}</p>}
              <p className="mt-2 text-[11px] text-slate-500">Whole trip or per person — our team will clarify on WhatsApp.</p>
            </div>
          </div>

          <div>
            <span className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2.5">Interests (select any)</span>
            <div className="flex flex-wrap gap-2" data-testid="enquiry-interests-chips">
              {INTEREST_OPTIONS.map((i) => {
                const on = form.interests.includes(i);
                return (
                  <button key={i} type="button" onClick={() => toggleInterest(i)}
                    data-testid={`interest-chip-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    aria-pressed={on}
                    className={`px-4 py-2.5 rounded-full text-[13px] font-medium border transition-[background-color,border-color,color,transform] duration-300 active:scale-95 ${
                      on ? "bg-gold text-white border-gold" : "border-[#0D1E36]/20 text-slate-300 hover:border-gold/60 hover:text-gold-light"
                    }`}>
                    {i}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-xs uppercase tracking-[0.16em] text-slate-400 mb-2">Additional Requirements (optional)</label>
            <textarea id="notes" data-testid="enquiry-notes-textarea" rows="2" className={`${inputCls} resize-none`}
              placeholder="Anniversary surprise, kid-friendly stay, specific hotel preference..."
              value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="enquiry-consent-label">
              <input type="checkbox" data-testid="enquiry-consent-checkbox" checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                className="mt-1 w-4 h-4 rounded accent-[#DC2626]" />
              <span className="text-xs text-slate-400 leading-relaxed">
                I agree to be contacted by RAJ TRAVELS GOA on phone / WhatsApp regarding this enquiry and my holiday plan.
              </span>
            </label>
            {errors.consent && <p className="text-xs text-red-500 mt-1.5" data-testid="error-consent">{errors.consent}</p>}
          </div>

          <button type="submit" disabled={sending} data-testid="enquiry-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-gold text-white font-semibold px-8 rounded-full text-sm sm:text-base hover:bg-gold-hover hover:shadow-[0_0_40px_rgba(220,38,38,0.35)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 py-4">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {sending ? "Preparing your plan..." : "Get My Goa Holiday Plan"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
